import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import Image from 'next/image';
import './EventCard.css';
import { formatNumber } from '@/shared/utils/format/formatNumber.mjs';
import { PACE_COLORS, FACTION_COLORS } from '@/shared/enums/colors.mjs';
import { SECTOR_COUNT } from '@/shared/enums/worlds.mjs';
import { countCapturedRegions } from '@/shared/utils/game/countCapturedRegions.mjs';
import AnimatedStat from '@/shared/components/AnimatedStat/AnimatedStat';
import { CAMPAIGN_STATUS, EVENT_STATUS, MAP_STATUS } from '@/shared/enums/events.mjs';
import factions from '@/shared/enums/factions.mjs';
import { formatCompactDuration } from '@/shared/utils/format/formatCompactDuration.mjs';
const passThrough = v => v == null ? '—' : String(v);
const formatSectorPct = v => Number.isFinite(v) ? `${Math.max(0, Math.min(100, v)).toFixed(1)}%` : '—';

/**
 * Compute frontier progress from campaign-level live data and map state.
 * Uses campaign points (not map sectors) so failed defends don't zero out progress.
 *
 * @param {object} campaignData - { points, points_max, status, enemy }
 * @param {object} factionMap - mapState[factionIndex] with sector data (for region names)
 * @returns {{ sector: number, region: string, percent: number, points: number, pointsMax: number, event: string } | null}
 */
export function computeFrontier(campaignData, factionMap) {
  if (!campaignData || !factionMap || campaignData.status !== CAMPAIGN_STATUS.ACTIVE) return null;
  const pointsMax = campaignData.points_max > 0 ? campaignData.points_max : 1;
  const points = campaignData.points;
  const pointsPerSector = pointsMax / SECTOR_COUNT;
  const sectorsEarned = Math.trunc(points / pointsPerSector);
  const frontier = sectorsEarned + 1;
  if (frontier > SECTOR_COUNT) return null; // all sectors captured

  const pointsIntoFrontier = points - sectorsEarned * pointsPerSector;
  const percent = pointsIntoFrontier / pointsPerSector * 100;
  const sectorData = factionMap[frontier];
  return {
    sector: frontier,
    region: sectorData?.region || `Sector ${frontier}`,
    percent,
    points: Math.round(pointsIntoFrontier),
    pointsMax: Math.round(pointsPerSector),
    event: sectorData?.event || ''
  };
}
function EventCountdown(t0) {
  const $ = _c(10);
  const {
    endTime
  } = t0;
  let t1;
  if ($[0] !== endTime) {
    t1 = () => endTime - Math.floor(Date.now() / 1000);
    $[0] = endTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [remaining, setRemaining] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== endTime) {
    t2 = () => {
      setRemaining(endTime - Math.floor(Date.now() / 1000));
      const id = setInterval(() => {
        setRemaining(endTime - Math.floor(Date.now() / 1000));
      }, 1000);
      return () => clearInterval(id);
    };
    t3 = [endTime];
    $[2] = endTime;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (remaining <= 0) {
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <span className="sector-card-countdown">Expired</span>;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t4;
  if ($[6] !== remaining) {
    t4 = formatCompactDuration(remaining);
    $[6] = remaining;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const text = t4;
  let t5;
  if ($[8] !== text) {
    t5 = <span className="sector-card-countdown" suppressHydrationWarning={true}>{text} left</span>;
    $[8] = text;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function SegmentCell(t0) {
  const $ = _c(6);
  const {
    seg
  } = t0;
  const status = seg?.status;
  if (status === MAP_STATUS.CAPTURED) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="sector-card-segment sector-card-segment--captured" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (status === MAP_STATUS.IN_PROGRESS) {
    const pct = Math.max(0, Math.min(100, seg?.percent ?? 0));
    const t1 = `${pct}%`;
    let t2;
    if ($[1] !== t1) {
      t2 = <div className="sector-card-segment sector-card-segment--in-progress" style={{
        "--segment-percent": t1
      }} />;
      $[1] = t1;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (status === EVENT_STATUS.ACTIVE) {
    const pct_0 = Math.max(0, Math.min(100, seg?.percent ?? 0));
    const t1 = `${pct_0}%`;
    let t2;
    if ($[3] !== t1) {
      t2 = <div className="sector-card-segment sector-card-segment--active" style={{
        "--segment-percent": t1
      }} />;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="sector-card-segment" />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/**
 * Pace status indicator — a ▲/▼/▪ glyph plus the points delta, colour-coded
 * by status. Mirrors the StatGrid delta-subtitle pattern. The glyph and the
 * number are separate inline-flex children, so the slot counter animates only
 * the digits and the gap between glyph and number is never compressed (the
 * spacing bug the old "175,259 behind" single-string label suffered from).
 *
 * The delta always renders; ▪ means dead-on (zero points either way), so the
 * glyph tracks the number rather than the buffered status band.
 *
 * @param {object} props - Component props
 * @param {{ status: 'ahead'|'behind'|'on_track', delta: number }} props.pace - Evaluated event pace
 */
function PaceIndicator(t0) {
  const $ = _c(12);
  const {
    pace
  } = t0;
  const key = pace.delta === 0 ? "on_track" : pace.status === "behind" ? "behind" : "ahead";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ahead: "\u25B2",
      behind: "\u25BC",
      on_track: "\u25AA"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const glyph = t1[key];
  const nudge = key === "on_track" ? "" : "-translate-y-[1.5px]";
  const t2 = PACE_COLORS[key];
  let t3;
  if ($[1] !== t2) {
    t3 = {
      color: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== glyph || $[4] !== nudge) {
    t4 = <span className={nudge}>{glyph}</span>;
    $[3] = glyph;
    $[4] = nudge;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== pace.delta) {
    t5 = <AnimatedStat value={pace.delta} />;
    $[6] = pace.delta;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <span className="sector-card-pace inline-flex items-center gap-1" style={t3} suppressHydrationWarning={true}>{t4}{t5}</span>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/**
 * The discriminated result of `attackForecast` or `sectorForecast`. Declared
 * locally rather than imported so this presentational component keeps no
 * dependency on the dashboard feature that computes it. Covers both the
 * percentile-window shape (attack/homeworld assaults) and the median-only
 * shape (sector ETAs, whose range hasn't shipped yet — see script 13).
 *
 * @typedef {{mode: 'window', p25: number, p50: number, p75: number, imminent: boolean}
 *   | {mode: 'median', p50: number, remaining: number, imminent: boolean}
 *   | {mode: 'hidden', reason: string}} AssaultForecast
 */

/**
 * @param {number} h hours
 * @returns {string} whole days at >=48h ("5d"), whole hours at >=1h, whole
 *   minutes below ("40m")
 */
function formatEtaHours(h) {
  const clamped = Math.max(0, h);
  if (clamped < 1) return `${Math.round(clamped * 60)}m`;
  if (clamped < 48) return `${Math.round(clamped)}h`;
  return `${Math.round(clamped / 24)}d`;
}

/**
 * Range with the unit written once when both bounds share it: `4-16h`,
 * `30-55m` — and per-bound only when mixed: `30m-2h`.
 *
 * @param {number} lo hours
 * @param {number} hi hours
 * @returns {string}
 */
function formatEtaRange(lo, hi) {
  const a = formatEtaHours(lo);
  const b = formatEtaHours(hi);
  return a.slice(-1) === b.slice(-1) ? `${a.slice(0, -1)}-${b}` : `${a}-${b}`;
}

/**
 * ETA line, rendered as one more middot-separated item in the meta row.
 *
 * Median-first with the range in parens for window forecasts: `~9h (4-16h)`.
 * The `~` and the range keep the single number honest — still not a
 * countdown. NOT a `±` form: the window is asymmetric (near campaign
 * completion p75 runs up to 2x the median, see /docs/predict), and a
 * symmetric spread would understate the late side. Median-only forecasts
 * (sector ETAs) render without a range — an unmeasured spread would be
 * worse than none.
 *
 * @param {object} props
 * @param {{mode: 'window', p25: number, p50: number, p75: number, imminent: boolean}
 *   | {mode: 'median', p50: number, remaining: number, imminent: boolean}} props.forecast
 */
function EtaLine(t0) {
  const $ = _c(14);
  const {
    forecast
  } = t0;
  let t1;
  if ($[0] !== forecast.p50) {
    t1 = formatEtaHours(forecast.p50);
    $[0] = forecast.p50;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const med = t1;
  let t2;
  if ($[2] !== forecast.mode || $[3] !== forecast.p25 || $[4] !== forecast.p75) {
    t2 = forecast.mode === "window" ? ` (${formatEtaRange(forecast.p25, forecast.p75)})` : "";
    $[2] = forecast.mode;
    $[3] = forecast.p25;
    $[4] = forecast.p75;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const range = t2;
  const rough = forecast.p50 >= 48 ? " Rough at multi-day range." : "";
  let t3;
  if ($[6] !== forecast.mode || $[7] !== forecast.p50) {
    t3 = forecast.mode === "window" ? `Assault ETA — median ${forecast.p50.toFixed(1)}h. Range is the 25th-75th percentile.` : `Median estimate ${forecast.p50.toFixed(1)}h. Range ships once calibrated (script 13).`;
    $[6] = forecast.mode;
    $[7] = forecast.p50;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const title = t3 + rough;
  const t4 = "sector-card-assault" + (forecast.imminent ? " sector-card-assault--imminent" : "");
  let t5;
  if ($[9] !== med || $[10] !== range || $[11] !== t4 || $[12] !== title) {
    t5 = <span className={t4} title={title} suppressHydrationWarning={true}>ETA ~{med}{range}</span>;
    $[9] = med;
    $[10] = range;
    $[11] = t4;
    $[12] = title;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

/**
 * How an active event is going to END, in the same left-aligned style as the
 * campaign/sector `EtaLine`.
 *
 * Behind, the bar never fills, so there is no ETA to show and the word carries
 * it in danger red — the `EventCountdown` beside this already holds the loss
 * time, so no second copy of the duration.
 *
 * On track or ahead, the fill ETA is the whole message: no word, just the
 * number — primary yellow on track, success green when the pace is ahead.
 *
 * @param {object} props
 * @param {{etaHours: number|null, onTrack: boolean, stalled: boolean}} props.eta
 * @param {{status: 'ahead'|'behind'|'on_track'}} [props.pace]
 */
function EventEta(t0) {
  const $ = _c(6);
  const {
    eta,
    pace
  } = t0;
  if (!eta.onTrack) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="sector-card-assault sector-card-assault--imminent" title="At the average pace since the event started, the bar will not fill before the timer runs out. The countdown is the time left." suppressHydrationWarning={true}>Behind</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const hours = eta.etaHours;
  const t1 = "sector-card-assault" + (pace?.status === "ahead" ? " sector-card-assault--ahead" : "");
  let t2;
  if ($[1] !== hours) {
    t2 = formatEtaHours(hours);
    $[1] = hours;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <span className={t1} title={"At the average pace since the event started, the bar fills before the timer runs out \u2014 this is when."} suppressHydrationWarning={true}>~{t2}</span>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export default function EventCard(t0) {
  const $ = _c(72);
  const {
    action,
    region,
    percent,
    points,
    pointsMax,
    factionIndex,
    pace,
    endTime,
    barLabel,
    pulseDelay,
    view: t1,
    factionMap,
    etaForecast: t2,
    eventEta: t3,
    alert: t4
  } = t0;
  const view = t1 === undefined ? "sector" : t1;
  const etaForecast = t2 === undefined ? null : t2;
  const eventEta = t3 === undefined ? null : t3;
  const alert = t4 === undefined ? false : t4;
  const showEventEta = eventEta?.mode === "verdict";
  const color = FACTION_COLORS[factionIndex] || "var(--color-primary)";
  const isEvent = !!endTime;
  const isDefending = action === "defending";
  const titleColor = isEvent ? "var(--color-danger)" : undefined;
  const barColor = isDefending ? "var(--color-danger)" : color;
  let t5;
  if ($[0] !== percent) {
    t5 = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
    $[0] = percent;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const safePct = t5;
  const isCampaign = view === "campaign";
  let t6;
  if ($[2] !== factionMap || $[3] !== isCampaign) {
    t6 = isCampaign ? countCapturedRegions(factionMap) : {
      captured: 0
    };
    $[2] = factionMap;
    $[3] = isCampaign;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const {
    captured
  } = t6;
  const isFactionTitle = isCampaign && !isEvent;
  const title = isFactionTitle ? factions[factionIndex]?.name || region : region;
  let cardStyle;
  if ($[5] !== color || $[6] !== pulseDelay) {
    cardStyle = {
      "--accent-color": color,
      "--faction-color": color
    };
    if (pulseDelay != null) {
      cardStyle["--pulse-delay"] = `${pulseDelay}s`;
    }
    $[5] = color;
    $[6] = pulseDelay;
    $[7] = cardStyle;
  } else {
    cardStyle = $[7];
  }
  const t7 = `/icons/faction${factionIndex}.webp`;
  let t8;
  if ($[8] !== t7) {
    t8 = <Image src={t7} alt="" width={16} height={16} />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== isDefending || $[11] !== isEvent || $[12] !== isFactionTitle || $[13] !== titleColor) {
    t9 = !isFactionTitle && <span className={"sector-card-action" + (isEvent ? " sector-card-action-flash" : "")} style={titleColor ? {
      color: titleColor
    } : undefined}>{isDefending ? "Defending" : "Capturing"}</span>;
    $[10] = isDefending;
    $[11] = isEvent;
    $[12] = isFactionTitle;
    $[13] = titleColor;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== alert) {
    t10 = alert && <span className="sector-card-event-alert sector-card-action-flash" title="Event in progress in this faction's territory" aria-label="Event in progress">!</span>;
    $[15] = alert;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== title) {
    t11 = <span className="sector-card-title">{title}</span>;
    $[17] = title;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t8 || $[22] !== t9) {
    t12 = <div className="sector-card-header">{t8}{t9}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== barLabel || $[25] !== etaForecast || $[26] !== eventEta || $[27] !== pace || $[28] !== showEventEta) {
    t13 = (barLabel || etaForecast?.mode === "window" || etaForecast?.mode === "median" || showEventEta) && <div className="sector-card-bar-label-row"><span className="sector-card-bar-label-group">{barLabel && <span className="sector-card-bar-label">{barLabel}</span>}{(etaForecast?.mode === "window" || etaForecast?.mode === "median") && <>{barLabel && <span className="sector-card-sep">·</span>}<EtaLine forecast={etaForecast} /></>}{showEventEta && <>{barLabel && <span className="sector-card-sep">·</span>}<EventEta eta={eventEta} pace={pace} /></>}</span>{pace && <><span className="sector-card-sep">·</span><PaceIndicator pace={pace} /></>}</div>;
    $[24] = barLabel;
    $[25] = etaForecast;
    $[26] = eventEta;
    $[27] = pace;
    $[28] = showEventEta;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = `${title} ${action} progress`;
  const t15 = isCampaign ? captured : safePct;
  const t16 = isCampaign ? 11 : 100;
  let t17;
  if ($[30] !== barColor || $[31] !== factionMap || $[32] !== isCampaign || $[33] !== safePct) {
    t17 = isCampaign ? <div className="sector-card-segments">{Array.from({
        length: 11
      }, (_, i) => {
        const r = i + 1;
        return <SegmentCell key={r} seg={factionMap?.[r]} factionColor={barColor} />;
      })}</div> : <div className="sector-card-bar-fill" style={{
      width: `${safePct}%`,
      background: barColor
    }} />;
    $[30] = barColor;
    $[31] = factionMap;
    $[32] = isCampaign;
    $[33] = safePct;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16 || $[38] !== t17) {
    t18 = <div className="sector-card-bar" role="progressbar" aria-label={t14} aria-valuenow={t15} aria-valuemin={0} aria-valuemax={t16}>{t17}</div>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== captured || $[41] !== isCampaign || $[42] !== safePct) {
    t19 = <span className="sector-card-pct">{isCampaign ? <><AnimatedStat value={captured} format={passThrough} />/11</> : <AnimatedStat value={safePct} format={formatSectorPct} />}</span>;
    $[40] = captured;
    $[41] = isCampaign;
    $[42] = safePct;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18 || $[45] !== t19) {
    t20 = <div className="sector-card-bar-wrap">{t18}{t19}</div>;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== points) {
    t21 = <AnimatedStat value={points} />;
    $[47] = points;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== pointsMax) {
    t22 = formatNumber(pointsMax);
    $[49] = pointsMax;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t21 || $[52] !== t22) {
    t23 = <span className="sector-card-points">{t21} / {t22}</span>;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== endTime) {
    t24 = endTime && <EventCountdown endTime={endTime} />;
    $[54] = endTime;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== barLabel || $[57] !== pace) {
    t25 = !barLabel && pace && <><span className="sector-card-sep">·</span><PaceIndicator pace={pace} /></>;
    $[56] = barLabel;
    $[57] = pace;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t23 || $[60] !== t24 || $[61] !== t25) {
    t26 = <div className="sector-card-meta">{t23}{t24}{t25}</div>;
    $[59] = t23;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t12 || $[64] !== t13 || $[65] !== t20 || $[66] !== t26) {
    t27 = <div className="sector-card-content">{t12}{t13}{t20}{t26}</div>;
    $[63] = t12;
    $[64] = t13;
    $[65] = t20;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="sector-card-accent" />;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== cardStyle || $[70] !== t27) {
    t29 = <div className="sector-card" style={cardStyle}>{t27}{t28}</div>;
    $[69] = cardStyle;
    $[70] = t27;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  return t29;
}